/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { ApiQuery } from '@app/core/http/api-query';
import { FormGroup } from '@angular/forms';
import { EventGroup } from '@app/events/events-groups/event-group.model';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { EventGroupsService } from '@app/events/events-groups/event-groups.service';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { EventGroupsFormService } from '@app/events/events-groups/event-groups-form.service';
import { Logger } from '@app/core';

const log = new Logger('EventGroupSelectComponent');

@Component({
  selector: 'app-event-group-select',
  templateUrl: './event-group-select.component.html',
  styleUrls: ['./event-group-select.component.scss'],
})
export class EventGroupSelectComponent implements OnInit {
  data: Observable<EventGroup[]>;
  apiQuery: ApiQuery = new ApiQuery();
  loader: boolean;
  @Input() parentFormGroup: FormGroup;
  @Input() includes: string = null;
  input$ = new Subject<string>();
  @Output() change = new EventEmitter();
  @Output() select = new EventEmitter();
  @Input() hideLabel = false;
  @Input() onlyActive = true;
  lastResponse: any = null;
  page = 1;

  constructor(private eventGroupsService: EventGroupsService, private eventGroupsFormService: EventGroupsFormService) {}

  ngOnInit() {
    const preSelectedEventGroup = this.parentFormGroup.get('event_group');
    const preSelectedEventGroupId = this.parentFormGroup.get('event_group_id');
    /**
     * Попълва избрания eventGroup на едит.
     * Ако има FormControl 'eventGroup' в parentFormGroup.
     * Ако няма ще се опита да го get-не по ID
     */
    if (preSelectedEventGroup) {
      /*
       * Следи за промени в eventGroup FormControl-a и когато намери валиден EventGroup спира да следи
       */
      const preselectedEventGroupSubscriber = preSelectedEventGroup.valueChanges.subscribe((eventGroup: any) => {
        if (eventGroup && eventGroup.name) {
          this.data = of([eventGroup]);
          preselectedEventGroupSubscriber.unsubscribe();
        }
      });
    } else if (preSelectedEventGroupId) {
      /*
       * Ако не е намерен eventGroup FormControl-a търси eventGroup_id FormControl и се опитва да get-не eventGroup-a по ID
       */
      const preselectedEventGroupIdSubscriber = preSelectedEventGroupId.valueChanges.subscribe((eventGroupId: any) => {
        if (eventGroupId) {
          this.loadById(eventGroupId);
          preselectedEventGroupIdSubscriber.unsubscribe();
        }
      });
    }
    this.eventGroupSearch();
  }

  // loadEventGroups(keyword?: string) {
  //   this.loader = true;
  //
  //   this.eventGroupsService.index(this.apiQuery.addFilter('name', keyword)).subscribe((response: PaginatedResponse) => {
  //     this.data = of(response.data);
  //     this.loader = false;
  //   });
  // }

  loadById(id: number) {
    log.debug('load by id:', id);
    this.loader = true;
    const query = new ApiQuery();

    if (this.includes) {
      query.addIncludes(this.includes);
    }
    this.eventGroupsService.show(id, query).subscribe((eventGroup: EventGroup) => {
      this.data = of([eventGroup]);
      this.loader = false;
      this.select.emit(eventGroup);
    });
  }

  eventGroupSearch() {
    if (this.includes) {
      this.apiQuery.addIncludes(this.includes);
    }
    if (this.onlyActive) {
      this.apiQuery.addFilter('active', true);
    }

    this.input$
      .pipe(
        tap(() => (this.loader = true)),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term: any) => {
          // За да ме прави излишен request след като се избере нещо
          if (!term) {
            return of(this.lastResponse);
          }

          this.apiQuery.addFilters({ name: term });
          return this.eventGroupsService.index(this.apiQuery).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.loader = false))
          );
        })
      )
      .subscribe((eventGroups: PaginatedResponse) => {
        this.data = of(eventGroups.data);
        this.lastResponse = eventGroups;
        this.loader = false;
        this.page = 1;
      });
  }

  add(searchTerm: string) {
    this.eventGroupsFormService.open(new EventGroup().deserialize({ name: searchTerm })).then(
      (response) => {
        this.loadById(response.id);
        this.parentFormGroup.get('event_group_id').setValue(response.id);
      },
      (err) => {
        log.error(err);
      }
    );
  }

  onChange($event: Event | {}) {
    this.change.emit($event);
    this.select.emit($event);
    this.loader = false;
  }

  edit() {
    if (this.parentFormGroup.get('event_group_id').value) {
      this.eventGroupsFormService
        .open(new EventGroup().deserialize({ id: this.parentFormGroup.get('event_group_id').value }))
        .then(
          (response) => {
            log.debug('response', response);
            this.loadById(response.id);
            this.parentFormGroup.get('event_group_id').setValue(response.id);
            if (this.parentFormGroup.get('event_group')) {
              this.parentFormGroup.get('event_group').patchValue(response);
            }
          },
          (err) => {
            log.error(err);
          }
        );
    }
  }

  loadMore() {
    if (this.page >= this.lastResponse.meta.last_page) {
      return false;
    }
    this.loader = true;
    this.page = this.page + 1;
    this.eventGroupsService.index(this.apiQuery.setPage(this.page)).subscribe((event: PaginatedResponse) => {
      this.data.subscribe(
        (res) => {
          this.data = of([...res, ...event.data]);
          this.loader = false;
        },
        () => {
          this.loader = false;
        }
      );
    });
  }
}
